/**
 * HIPAA Utility Functions
 * 
 * These utilities help mask Patient Personally Identifiable Information (PII)
 * to comply with HIPAA privacy regulations when displaying data to certain users.
 */

/**
 * Masks an email address, showing only the first character and domain
 * Example: "john.doe@example.com" -> "j***@example.com"
 */
export const maskEmail = (email: string | null | undefined): string => {
  if (!email) return '***@***.***';
  
  const [localPart, domain] = email.split('@');
  if (!domain) return '***@***.***';
  
  const maskedLocal = localPart.charAt(0) + '***';
  return `${maskedLocal}@${domain}`;
};

/**
 * Masks a phone number, showing only the last 4 digits
 * Example: "+1 (555) 123-4567" -> "***-***-4567"
 */
export const maskPhone = (phone: string | null | undefined): string => {
  if (!phone) return '***-***-****';
  
  // Extract only digits
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***-***-****';
  
  const lastFour = digits.slice(-4);
  return `***-***-${lastFour}`;
};

/**
 * Masks a full name, showing only first initial and last initial
 * Example: "John Doe" -> "J*** D***"
 */
export const maskName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
  const maskedFirst = firstName ? `${firstName.charAt(0)}***` : '***';
  const maskedLast = lastName ? `${lastName.charAt(0)}***` : '***';
  return `${maskedFirst} ${maskedLast}`;
};

/**
 * Masks a date of birth, showing only the year
 * Example: "1990-05-15" -> "****-**-** (1990)"
 */
export const maskDOB = (dob: string | null | undefined): string => {
  if (!dob) return '****-**-**';
  
  const year = dob.split('-')[0];
  return `****-**-** (${year})`;
};

/**
 * Masks an address, showing only the city/state if available
 * Example: "123 Main St, Los Angeles, CA 90210" -> "***, Los Angeles, CA ***"
 */
export const maskAddress = (address: string | null | undefined): string => {
  if (!address) return '***';
  
  // Try to extract city/state pattern
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    // Mask the street address but show city
    return `***, ${parts.slice(1, -1).join(', ')}, ***`;
  }
  
  return '***';
};

/**
 * Partially masks a string, keeping the first and last characters visible
 * Example: "ABC123DEF" -> "A*******F"
 */
export const partialMask = (value: string | null | undefined, showChars: number = 1): string => {
  if (!value) return '***';
  if (value.length <= showChars * 2) return '*'.repeat(value.length);
  
  const start = value.slice(0, showChars);
  const end = value.slice(-showChars);
  const middle = '*'.repeat(Math.min(value.length - (showChars * 2), 6));
  
  return `${start}${middle}${end}`;
};

/**
 * Interface for patient data that can be masked
 */
export interface MaskedPatientData {
  displayName: string;
  displayEmail: string;
  displayPhone: string;
  displayDOB: string;
  displayAddress?: string;
}

/**
 * Masks all PII fields of a patient record
 */
export const maskPatientPII = (
  patient: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    dob?: string | null;
    address?: string | null;
  }
): MaskedPatientData => {
  return {
    displayName: maskName(patient.first_name, patient.last_name),
    displayEmail: maskEmail(patient.email),
    displayPhone: maskPhone(patient.phone),
    displayDOB: maskDOB(patient.dob),
    displayAddress: patient.address ? maskAddress(patient.address) : undefined,
  };
};

/**
 * Determines if PII should be masked based on user role and context
 * Super Admins and Agency Admins can see full data
 * Supervisors may have limited access
 * Other users should see masked data
 */
export const shouldMaskPII = (
  userRoles: string[],
  isImpersonating: boolean = false
): boolean => {
  // Super admins always see full data
  if (userRoles.includes('super_admin')) return false;
  
  // Agency admins see full data for their agency
  if (userRoles.includes('agency_admin')) return false;
  
  // Supervisors see full data for assigned patients
  if (userRoles.includes('supervisor')) return false;
  
  // When impersonating, follow the impersonated user's permissions
  if (isImpersonating) return true;
  
  // Default: mask PII for other users
  return true;
};
